/** Paramètres utilisateur : identité, langue, fuseau, rappels, état du système. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { miscApi } from "../api/endpoints";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DefinitionList,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextField,
} from "../components/ui";
import { useSession } from "../hooks/SessionContext";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { formatDateTime } from "../lib/format";

const TIMEZONES = [
  { value: "Europe/Paris", label: "Europe/Paris (heure française)" },
  { value: "Europe/Brussels", label: "Europe/Bruxelles" },
  { value: "Europe/Luxembourg", label: "Europe/Luxembourg" },
  { value: "Europe/Zurich", label: "Europe/Zurich" },
  { value: "Indian/Reunion", label: "Indian/La Réunion" },
  { value: "America/Guadeloupe", label: "Amérique/Guadeloupe" },
  { value: "UTC", label: "UTC" },
];

const LOCALES = [{ value: "fr-FR", label: "Français (France)" }];

export function SettingsPage() {
  useDocumentTitle("Paramètres");
  const toast = useToast();
  const { user, refresh, currentBusiness } = useSession();
  const health = useResource(() => miscApi.health(), []);

  const [displayName, setDisplayName] = useState("");
  const [timezone, setTimezone] = useState("Europe/Paris");
  const [locale, setLocale] = useState("fr-FR");

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name ?? "");
    setTimezone(user.timezone);
    setLocale(user.locale);
  }, [user]);

  const save = useMutation(
    (patch: Record<string, unknown>) => miscApi.updateSettings(patch),
    {
      onSuccess: async () => {
        await refresh();
        toast.success("Paramètres enregistrés");
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  if (!user) return <LoadingBlock />;

  const dirty =
    displayName !== (user.display_name ?? "") ||
    timezone !== user.timezone ||
    locale !== user.locale;

  return (
    <>
      <PageHeader
        title="Paramètres"
        subtitle="Votre compte, l'affichage et les rappels."
      />

      <div className="stack">
        {!user.email_verified && (
          <Alert tone="warning" title="Adresse e-mail non vérifiée">
            Les rappels par e-mail ne peuvent pas être envoyés tant que votre
            adresse n'est pas vérifiée.
          </Alert>
        )}

        <ErrorAlert error={save.error} title="Enregistrement refusé" />

        <Card title="Mon compte">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void save.run({
                display_name: displayName.trim() || null,
                timezone,
                locale,
              });
            }}
            noValidate
          >
            <TextField
              label="Nom affiché"
              optional
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              error={save.fieldErrors.display_name}
            />
            <SelectField
              label="Langue de l'interface"
              options={LOCALES}
              value={locale}
              onChange={(event) => setLocale(event.target.value)}
              hint="L'interface est en français. D'autres langues pourront être ajoutées."
            />
            <SelectField
              label="Fuseau horaire"
              options={TIMEZONES}
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              hint="Utilisé pour afficher les dates et calculer les rappels."
            />
            <Button type="submit" variant="primary" loading={save.pending} disabled={!dirty}>
              Enregistrer
            </Button>
          </form>
        </Card>

        <Card title="Rappels par e-mail">
          <Checkbox
            label="Recevoir les rappels d'échéance par e-mail"
            description="Le détail des paliers se règle depuis la page Notifications."
            checked={user.email_notifications_enabled}
            onChange={(value) =>
              void save.run({ email_notifications_enabled: value })
            }
          />
          <Link to="/notifications" className="btn btn--ghost btn--sm mt-3">
            Régler les paliers de rappel
          </Link>
        </Card>

        <Card title="Informations du compte">
          <DefinitionList
            items={[
              { term: "Adresse e-mail", value: user.email },
              {
                term: "Adresse vérifiée",
                value: user.email_verified ? "Oui" : "Non",
              },
              {
                term: "Vérification en deux étapes",
                value: user.has_twofa
                  ? user.twofa_methods
                      .filter((m) => m.enabled)
                      .map((m) => m.label)
                      .join(", ")
                  : "Non activée",
              },
              {
                term: "Codes de récupération restants",
                value: String(user.recovery_codes_remaining),
              },
              {
                term: "Entreprise courante",
                value: currentBusiness?.trade_name ?? "—",
              },
              {
                term: "Dernière connexion",
                value: formatDateTime(user.last_login_at),
              },
              {
                term: "Compte créé le",
                value: formatDateTime(user.created_at),
              },
            ]}
          />
          <Link to="/securite" className="btn btn--ghost btn--sm mt-4">
            Gérer la sécurité de mon compte
          </Link>
        </Card>

        <Card title="État du système">
          {health.loading ? (
            <LoadingBlock />
          ) : health.error ? (
            <ErrorAlert error={health.error} title="État indisponible" />
          ) : health.data ? (
            <>
              <DefinitionList
                items={[
                  {
                    term: "Application",
                    value: `${health.data.app_name} ${health.data.app_version}`,
                  },
                  { term: "Environnement", value: health.data.environment },
                  {
                    term: "Base de données",
                    value:
                      health.data.database === "ok"
                        ? "Accessible"
                        : "Inaccessible",
                  },
                  {
                    term: "Format des données",
                    value: `version ${health.data.data_schema_version}`,
                  },
                  {
                    term: "Bibliothèque administrative",
                    value: health.data.rules_library_version ?? "non installée",
                  },
                ]}
              />
              {health.data.warnings.length > 0 && (
                <Alert tone="warning" title="Avertissements de configuration">
                  <ul className="mb-0">
                    {health.data.warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </>
          ) : null}
        </Card>
      </div>
    </>
  );
}
